import React from "react";

export const AppSummary = ({
	data = [],
	selectedResults = [],
	totalItems = 0,
	performanceMetric = "downloads",
	isHighPerformanceSet = false,
}) => {
	const getPerformanceMetricInfo = (metric) => {
		const metricInfo = {
			downloads: {
				title: "High Download Apps",
				description: "Apps with maximum download volumes",
				icon: "download",
				unit: "M",
			},
			rating: {
				title: "High Rating Apps",
				description: "Apps with exceptional user ratings",
				icon: "star",
				unit: "★",
			},
			ctr: {
				title: "High CTR Apps",
				description: "Apps with superior click-through rates",
				icon: "touch_app",
				unit: "%",
			},
			vtr: {
				title: "High VTR Apps",
				description: "Apps with excellent view-through rates",
				icon: "play_circle",
				unit: "%",
			},
			viewability: {
				title: "High Viewability Apps",
				description: "Apps optimized for maximum viewability rates",
				icon: "visibility",
				unit: "%",
			},
			revenue: {
				title: "High Revenue Apps",
				description: "Apps with superior revenue per impression",
				icon: "attach_money",
				unit: "$",
			},
		};

		return metricInfo[metric] || metricInfo.downloads;
	};

	// FIXED: Calculate real metrics from actual API app data using correct field mappings
	const calculateMetrics = () => {
		if (!data || data.length === 0) {
			return {
				totalMarketRequests: "0M",
				avgViewability: "0%",
				avgCTR: "0%",
				avgVTR: "0%",
				totalDownloads: "0M",
				avgRating: "0",
				avgRevenuePerImp: "$0.000",
				totalImpressions: "0M",
				appCount: 0,
			};
		}

		const formatNumber = (value) => {
			if (value >= 1000000000) return `${(value / 1000000000).toFixed(1)}B`;
			if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
			if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
			return value.toString();
		};

		const formatCurrency = (value) => {
			if (value >= 1) return `${value.toFixed(2)}`;
			return `${value.toFixed(3)}`;
		};

		// FIXED: Calculate Total Market Requests using same logic as AppResultsList
		const totalMarketRequests = data.reduce((sum, app) => {
			const rawValue =
				app.totalCprMarketRequests ||
				app._original?.totalCprMarketRequests ||
				app.marketRequests;

			if (!rawValue || rawValue === "n/a") return sum;

			const numericValue = parseFloat(rawValue);
			return sum + (isNaN(numericValue) ? 0 : numericValue);
		}, 0);

		// FIXED: Calculate Total Downloads
		const totalDownloads = data.reduce((sum, app) => {
			const downloads = parseFloat(app.downloads) || 0;
			return sum + downloads;
		}, 0);

		// FIXED: CTR calculation using same logic as AppResultsList
		const totalClicks = data.reduce((sum, app) => {
			return sum + (app.totalCprClicks || 0);
		}, 0);

		console.log("totalClicks--", totalClicks);

		const totalImpressionsWithClickMacro = data.reduce((sum, app) => {
			return sum + (app.totalImpressionsWithClickMacro || 0);
		}, 0);

		console.log(
			"totalImpressionsWithClickMacro--",
			totalImpressionsWithClickMacro,
		);

		const avgCTR =
			totalImpressionsWithClickMacro > 0
				? totalClicks / totalImpressionsWithClickMacro
				: 0;

		// FIXED: VTR calculation using same logic as AppResultsList
		const totalCompleteFirstOccurrence = data.reduce((sum, app) => {
			return (
				sum +
				(app.totalCompleteFirstOccurrence ||
					app._original?.totalCompleteFirstOccurrence ||
					0)
			);
		}, 0);

		const totalStartFirstOccurrence = data.reduce((sum, app) => {
			return (
				sum +
				(app.totalStartFirstOccurrence ||
					app._original?.totalStartFirstOccurrence ||
					0)
			);
		}, 0);

		const avgVTR =
			totalStartFirstOccurrence > 0
				? (totalCompleteFirstOccurrence / totalStartFirstOccurrence) * 100
				: 0;

		// FIXED: Viewability calculation using same logic as AppResultsList
		const totalViewables = data.reduce((sum, app) => {
			return sum + (app.totalViewables || app._original?.totalViewables || 0);
		}, 0);

		const totalViewabilityMeasures = data.reduce((sum, app) => {
			return (
				sum +
				(app.totalViewabilityMeasures ||
					app._original?.totalViewabilityMeasures ||
					0)
			);
		}, 0);

		const avgViewability =
			totalViewabilityMeasures > 0
				? (totalViewables / totalViewabilityMeasures) * 100
				: 0;

		// FIXED: Revenue per impression calculation
		const totalRevenue = data.reduce((sum, app) => {
			const revenueValue =
				app.totalCprMarketDemandPartnerSpendInUsd ||
				app.calculated_revenueperimpression ||
				app.revenuePerImp ||
				app.rpi ||
				0;
			return (
				sum +
				(typeof revenueValue === "number"
					? revenueValue
					: parseFloat(revenueValue) || 0)
			);
		}, 0);

		const totalImpressions = data.reduce((sum, app) => {
			return sum + (app.totalImpressions || app.impressions || 0);
		}, 0);

		const avgRevenuePerImp =
			totalImpressions > 0 ? totalRevenue / totalImpressions : 0;

		// FIXED: Average rating calculation
		const appsWithRating = data.filter((app) => app.rating && app.rating > 0);
		const avgRating =
			appsWithRating.length > 0
				? appsWithRating.reduce((sum, app) => sum + parseFloat(app.rating), 0) /
					appsWithRating.length
				: 0;

		const result = {
			totalMarketRequests: formatNumber(totalMarketRequests),
			avgViewability: `${avgViewability.toFixed(1)}%`,
			avgCTR: `${avgCTR.toFixed(3)}%`,
			avgVTR: `${avgVTR.toFixed(1)}%`,
			totalDownloads: formatNumber(totalDownloads),
			avgRating: avgRating > 0 ? avgRating.toFixed(1) : "0",
			avgRevenuePerImp: `$${formatCurrency(avgRevenuePerImp)}`,
			totalImpressions: formatNumber(totalImpressions),
			appCount: data.length,
		};

		// Debug logging
		console.log("=== SUMMARY CALCULATION DEBUG ===");
		console.log("Data length:", data.length);
		console.log("Total Market Requests:", totalMarketRequests);
		console.log("Total Impressions:", totalImpressions);
		console.log("Total Clicks:", totalClicks);
		console.log("CTR calculation:", avgCTR);
		console.log("Total Viewables:", totalViewables);
		console.log("Total Viewability Measures:", totalViewabilityMeasures);
		console.log("Viewability calculation:", avgViewability);
		console.log("Final result:", result);
		console.log("================================");

		return result;
	};

	const metrics = calculateMetrics();
	const metricInfo = getPerformanceMetricInfo(performanceMetric);

	const MetricCard = ({ icon, label, value, isHighlighted = false }) => (
		<div
			className={`as-metric-card ${isHighlighted ? "as-metric-card-highlighted" : ""}`}
		>
			<div
				className={`as-metric-icon ${isHighlighted ? "as-metric-icon-highlighted" : ""}`}
			>
				<span className="material-icons">{icon}</span>
			</div>
			<div className="as-metric-content">
				<div className="as-metric-label">{label}</div>
				<div className="as-metric-value">{value}</div>
			</div>
		</div>
	);

	const getHighlightedMetrics = () => {
		if (!isHighPerformanceSet) return {};

		switch (performanceMetric) {
			case "downloads":
				return { downloads: true };
			case "rating":
				return { rating: true };
			case "ctr":
				return { ctr: true };
			case "vtr":
				return { vtr: true };
			case "viewability":
				return { viewability: true };
			case "revenue":
				return { revenue: true };
			default:
				return {};
		}
	};

	const highlightedMetrics = getHighlightedMetrics();

	return (
		<>
			<style>
				{`
          /* Import Material Icons font */
          @import url('https://fonts.googleapis.com/icon?family=Material+Icons');

          .as-container {
            background: white;
            border-radius: 8px;
            padding: 24px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            margin-bottom: 12px;
            border: 1px solid #e5e7eb;
          }

          .as-header {
            margin-bottom: 20px;
          }

          .as-title {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 16px;
            font-weight: 600;
            color: #374151;
            margin-bottom: 4px;
          }

          .as-title .material-icons {
            font-size: 20px;
            color: #6b7280;
          }

          .as-subtitle {
            font-size: 14px;
            color: #6b7280;
            margin: 0;
          }

          .as-performance-badge {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            background: linear-gradient(135deg, #3b82f6, #1d4ed8);
            color: white;
            padding: 4px 8px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 500;
            margin-left: 8px;
          }

          .as-performance-badge .material-icons {
            font-size: 14px;
          }

          .as-metrics-grid {
            display: grid;
            grid-template-columns: repeat(6, 1fr);
            gap: 16px;
          }

          .as-metric-card {
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 20px 16px;
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            transition: all 0.2s;
            min-width: 0;
          }

          .as-metric-card:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.08);
            border-color: #d1d5db;
          }

          .as-metric-card-highlighted {
            background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
            border-color: #3b82f6;
            box-shadow: 0 4px 16px rgba(59, 130, 246, 0.15);
          }

          .as-metric-card-highlighted:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(59, 130, 246, 0.2);
          }

          .as-metric-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            border-radius: 8px;
            background: #f9fafb;
            border: 1px solid #e5e7eb;
            margin-bottom: 12px;
          }

          .as-metric-icon-highlighted {
            background: linear-gradient(135deg, #3b82f6, #1d4ed8);
            border-color: #1d4ed8;
          }

          .as-metric-icon-highlighted .material-icons {
            color: white !important;
          }

          .as-metric-icon .material-icons {
            font-size: 20px;
            color: #6b7280;
          }

          .as-metric-content {
            flex: 1;
            width: 100%;
          }

          .as-metric-label {
            font-size: 12px !important;
            font-weight: 500 !important;
            color: #6b7280 !important;
            margin-bottom: 8px !important;
            line-height: 1.3 !important;
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            text-align: center !important;
          }

          .as-metric-card-highlighted .as-metric-label {
            color: #1e40af !important;
            font-weight: 600 !important;
          }

          .as-metric-value {
            font-size: 18px !important;
            font-weight: 600 !important;
            color: #111827 !important;
            line-height: 1 !important;
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            text-align: center !important;
          }

          .as-metric-card-highlighted .as-metric-value {
            color: #1e40af !important;
            font-weight: 700 !important;
          }

          .as-empty-state {
            text-align: center;
            padding: 40px 20px;
            color: #6b7280;
          }

          .as-empty-state .material-icons {
            font-size: 48px;
            margin-bottom: 12px;
            color: #d1d5db;
            display: block;
          }

          /* Mobile Styles */
          @media (max-width: 768px) {
            .as-metrics-grid {
              grid-template-columns: repeat(3, 1fr);
              gap: 12px;
            }
            
            .as-metric-card {
              padding: 16px 12px;
            }
            
            .as-metric-value {
              font-size: 16px !important;
            }

            .as-metric-label {
              font-size: 11px !important;
            }

            .as-metric-icon {
              width: 36px;
              height: 36px;
              margin-bottom: 10px;
            }

            .as-metric-icon .material-icons {
              font-size: 18px;
            }
          }

          @media (max-width: 480px) {
            .as-container {
              padding: 16px;
            }
            
            .as-metrics-grid {
              grid-template-columns: repeat(2, 1fr);
              gap: 10px;
            }

            .as-metric-value {
              font-size: 14px !important;
            }

            .as-metric-label {
              font-size: 10px !important;
            }
          }
        `}
			</style>

			<div className="as-container">
				<div className="as-header">
					<div className="as-title">
						<span className="material-icons">apps</span>
						{isHighPerformanceSet
							? metricInfo.title
							: "App Performance Summary"}
						{isHighPerformanceSet && (
							<div className="as-performance-badge">
								<span className="material-icons">{metricInfo.icon}</span>
								Top {performanceMetric.toUpperCase()}
							</div>
						)}
					</div>
					<p className="as-subtitle">
						{isHighPerformanceSet
							? `${metricInfo.description} - ${metrics.appCount} apps`
							: `Real-time metrics for ${totalItems > 0 ? totalItems.toLocaleString() : metrics.appCount} apps`}
						{selectedResults.length > 0 &&
							` • ${selectedResults.length} selected`}
					</p>
				</div>

				{data.length > 0 ? (
					<div className="as-metrics-grid">
						<MetricCard
							icon="request_quote"
							label="Total Market Requests"
							value={metrics.totalMarketRequests}
							isHighlighted={highlightedMetrics.requests}
						/>

						<MetricCard
							icon="visibility"
							label="Avg. Viewability"
							value={metrics.avgViewability}
							isHighlighted={highlightedMetrics.viewability}
						/>

						<MetricCard
							icon="touch_app"
							label="Avg. CTR"
							value={metrics.avgCTR}
							isHighlighted={highlightedMetrics.ctr}
						/>

						<MetricCard
							icon="play_circle"
							label="Avg. VTR"
							value={metrics.avgVTR}
							isHighlighted={highlightedMetrics.vtr}
						/>

						<MetricCard
							icon="star"
							label="Total Impressions"
							value={metrics.totalImpressions}
							isHighlighted={highlightedMetrics.rating}
						/>

						<MetricCard
							icon="download"
							label="Total Downloads"
							value={metrics.totalDownloads}
							isHighlighted={highlightedMetrics.downloads}
						/>
					</div>
				) : (
					<div className="as-empty-state">
						<span className="material-icons">apps</span>
						<p>No app data available for summary</p>
					</div>
				)}
			</div>
		</>
	);
};
